import React, { useMemo } from 'react';

import { DEFAULT_CLIENT_ID, appRoutes } from '@/app/routes';
import { SidebarNavItem } from '@/components/layout/SidebarNav';
import {
  AssetLibraryIcon,
  AssessmentsIcon,
  PortfolioIcon,
  ProjectsIcon,
  SkillsIcon,
  WorkspaceIcon,
} from '@/icons';

export interface UseAppNavItemsParams {
  accessToken: string | null | undefined;
  selectedClientId: string | undefined;
  selectedProjectId: string | undefined;
  clientName?: string;
}

export interface UseAppNavItemsReturn {
  topNavItems: SidebarNavItem[];
  workspaceNavItems: SidebarNavItem[];
}

/**
 * Computes the top and workspace sidebar nav item arrays for the current route scope.
 * Re-evaluates only when auth state or the selected client/project changes.
 */
export function useAppNavItems({
  accessToken,
  selectedClientId,
  selectedProjectId,
  clientName,
}: UseAppNavItemsParams): UseAppNavItemsReturn {
  const topNavItems: SidebarNavItem[] = useMemo(() => {
    const items: SidebarNavItem[] = [
      {
        key: 'portfolio',
        label: 'Portfolio',
        path: appRoutes.portfolio,
        icon: React.createElement(PortfolioIcon),
      },
    ];

    items.push({
      key: 'skills',
      label: 'Skills',
      path: appRoutes.skills,
      icon: React.createElement(SkillsIcon),
    });

    if (selectedClientId && clientName) {
      items.push(
        { key: 'project-context-divider', divider: true },
        { key: 'project-context-client', label: clientName, disabled: true },
        {
          key: 'client-projects',
          label: 'Projects',
          path: appRoutes.projects(selectedClientId),
          icon: React.createElement(ProjectsIcon),
        },
      );
    }

    return items;
  }, [selectedClientId, clientName]);

  const workspaceNavItems: SidebarNavItem[] = useMemo(() => {
    if (!accessToken || !selectedProjectId) {
      return [];
    }

    const clientId = selectedClientId ?? DEFAULT_CLIENT_ID;

    return [
      {
        key: 'project-home',
        label: 'Overview',
        path: appRoutes.projectHome(clientId, selectedProjectId),
        icon: React.createElement(WorkspaceIcon),
      },
      {
        key: 'assessments',
        label: 'All Assessments',
        path: appRoutes.projectAssessments(selectedProjectId, clientId),
        icon: React.createElement(AssessmentsIcon),
      },
      {
        key: 'asset-library',
        label: 'Asset Library',
        path: appRoutes.projectAssetLibrary(selectedProjectId, clientId),
        icon: React.createElement(AssetLibraryIcon),
      },
    ];
  }, [accessToken, selectedClientId, selectedProjectId]);

  return { topNavItems, workspaceNavItems };
}
